import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, Inbox, Briefcase, Settings as SettingsIcon, LayoutDashboard, Trash2, Plus, Save, FileText } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/content";
import { Overline } from "@/components/Reveal";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "leads", label: "Leads & Pipelines", icon: Inbox },
  { key: "users", label: "Users", icon: Users },
  { key: "opportunities", label: "Opportunities", icon: Briefcase },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-white/10 hidden md:flex flex-col fixed h-full bg-[#0E0E0E]">
        <Link to="/" className="font-display text-xl font-extrabold p-6">MOAGLEE<span className="text-[#CC5500]">.</span></Link>
        <nav className="flex-1 px-3 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded transition-colors ${
                tab === t.key ? "bg-[#CC5500] text-white" : "text-neutral-400 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={doLogout} className="flex items-center gap-3 px-7 py-5 text-neutral-400 hover:text-white text-sm" data-testid="admin-logout">
          <LogOut size={18} /> Logout
        </button>
      </aside>

      <div className="flex-1 md:ml-60">
        <header className="border-b border-white/10 h-16 flex items-center justify-between px-6 sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-30">
          <div className="flex gap-2 md:hidden">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`p-2 ${tab === t.key ? "text-[#CC5500]" : "text-neutral-500"}`}>
                <t.icon size={18} />
              </button>
            ))}
          </div>
          <h1 className="font-display text-lg font-bold capitalize hidden md:block">{TABS.find((t) => t.key === tab)?.label}</h1>
          <span className="text-sm text-neutral-400">{user?.email}</span>
        </header>

        <main className="p-6">
          {tab === "overview" && <Overview />}
          {tab === "leads" && <Leads />}
          {tab === "users" && <UsersTab />}
          {tab === "opportunities" && <Opportunities />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <p className="text-neutral-500">Loading…</p>;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Total Leads" value={stats.total_leads} />
        <Stat label="Users" value={stats.total_users} />
        <Stat label="Opportunities" value={stats.total_opportunities} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="card-edge p-6">
          <Overline className="mb-4">Leads by Pipeline</Overline>
          {Object.entries(stats.by_pipeline).map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
        </div>
        <div className="card-edge p-6">
          <Overline className="mb-4">Leads by Status</Overline>
          {Object.entries(stats.by_status).map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Leads() {
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], pipelines: [] });
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/leads").then(({ data }) => setLeads(data)).catch(() => {});
  useEffect(() => { load(); api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const update = async (lead_id, patch) => {
    await api.patch(`/leads/${lead_id}`, patch);
    toast.success("Lead updated");
    load();
    if (selected?.lead_id === lead_id) setSelected({ ...selected, ...patch });
  };
  const remove = async (lead_id) => {
    await api.delete(`/leads/${lead_id}`);
    toast.success("Lead deleted");
    setSelected(null);
    load();
  };

  const shown = filter ? leads.filter((l) => l.pipeline === filter) : leads;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setFilter("")} className={`text-sm px-3 py-1.5 border ${!filter ? "border-[#CC5500] text-[#CC5500]" : "border-white/15 text-neutral-400"}`}>All</button>
        {meta.pipelines.map((p) => (
          <button key={p} onClick={() => setFilter(p)} className={`text-sm px-3 py-1.5 border ${filter === p ? "border-[#CC5500] text-[#CC5500]" : "border-white/15 text-neutral-400"}`} data-testid={`filter-${p}`}>{p}</button>
        ))}
      </div>

      <div className="card-edge overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-neutral-500 border-b border-white/10">
              <th className="p-4 font-medium">Name</th>
              <th className="p-4 font-medium hidden md:table-cell">Type</th>
              <th className="p-4 font-medium hidden lg:table-cell">Pipeline</th>
              <th className="p-4 font-medium">Status</th>
              <th className="p-4 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => (
              <tr key={l.lead_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`lead-row-${l.lead_id}`}>
                <td className="p-4">
                  <button onClick={() => setSelected(l)} className="text-left hover:text-[#CC5500]">
                    <p className="font-medium">{l.name}</p>
                    <p className="text-neutral-500 text-xs">{l.email}</p>
                  </button>
                </td>
                <td className="p-4 capitalize hidden md:table-cell text-neutral-400">{l.lead_type.replace(/_/g, " ")}</td>
                <td className="p-4 hidden lg:table-cell text-neutral-400">{l.pipeline}</td>
                <td className="p-4">
                  <select value={l.status} onChange={(e) => update(l.lead_id, { status: e.target.value })} className="bg-[#1A1A1A] border border-white/15 text-xs px-2 py-1.5 rounded" data-testid={`status-select-${l.lead_id}`}>
                    {meta.statuses.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-4">
                  <button onClick={() => remove(l.lead_id)} className="text-neutral-600 hover:text-red-400"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-neutral-500">No leads.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#141414] border border-white/10 max-w-lg w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-modal">
            <h3 className="font-display text-2xl font-bold">{selected.name}</h3>
            <p className="text-neutral-400">{selected.email} · {selected.phone}</p>
            <p className="overline text-[#CC5500] mt-4">{selected.pipeline}</p>
            <div className="mt-4 space-y-2 text-sm">
              <DetailRow label="Type" value={selected.lead_type.replace(/_/g, " ")} />
              <DetailRow label="Source" value={selected.source_page} />
              <DetailRow label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
              {Object.entries(selected.data || {}).filter(([k]) => k !== "documents").map(([k, v]) => (
                <DetailRow key={k} label={k.replace(/_/g, " ")} value={String(v)} />
              ))}
              {selected.notes && <DetailRow label="Notes" value={selected.notes} />}
            </div>
            {Array.isArray(selected.data?.documents) && selected.data.documents.length > 0 && (
              <div className="mt-5">
                <label className="overline text-neutral-400 mb-2 block">Documents</label>
                <ul className="space-y-2">
                  {selected.data.documents.map((d) => (
                    <li key={d.file_id}>
                      <a
                        href={`${API}/files/${d.file_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#CC5500] hover:underline"
                        data-testid={`doc-link-${d.file_id}`}
                      >
                        <FileText size={14} /> {d.filename}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <label className="overline text-neutral-400 mt-6 mb-2 block">Admin Notes</label>
            <textarea defaultValue={selected.admin_notes} className="field-input" rows={3} onBlur={(e) => update(selected.lead_id, { admin_notes: e.target.value })} data-testid="admin-notes-input" />
            <label className="overline text-neutral-400 mt-4 mb-2 block">Tags (comma separated)</label>
            <input defaultValue={(selected.tags || []).join(", ")} className="field-input" onBlur={(e) => update(selected.lead_id, { tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} data-testid="tags-input" />
            <button onClick={() => setSelected(null)} className="btn-ghost w-full justify-center mt-6">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setRole = async (user_id, role) => { await api.patch(`/admin/users/${user_id}/role`, { role }); toast.success("Role updated"); load(); };
  return (
    <div className="card-edge overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-neutral-500 border-b border-white/10">
            <th className="p-4 font-medium">Name</th><th className="p-4 font-medium hidden sm:table-cell">Email</th><th className="p-4 font-medium">Role</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-b border-white/5" data-testid={`user-row-${u.user_id}`}>
              <td className="p-4">{u.name}</td>
              <td className="p-4 text-neutral-400 hidden sm:table-cell">{u.email}</td>
              <td className="p-4">
                <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} className="bg-[#1A1A1A] border border-white/15 text-xs px-2 py-1.5 rounded" data-testid={`role-select-${u.user_id}`}>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EMPTY_OPP = { title: "", type: "CRE Listing", location: "", short_description: "", full_description: "", price_range: "", visibility: "public", cta_link: "", status: "active", image_url: "" };

function Opportunities() {
  const [opps, setOpps] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/opportunities/all").then(({ data }) => setOpps(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.opportunity_id) await api.put(`/opportunities/${editing.opportunity_id}`, editing);
      else await api.post("/opportunities", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { await api.delete(`/opportunities/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <button onClick={() => setEditing(EMPTY_OPP)} className="btn-primary mb-6" data-testid="add-opp-btn"><Plus size={16} /> Add Opportunity</button>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {opps.map((o) => (
          <div key={o.opportunity_id} className="card-edge p-5" data-testid={`admin-opp-${o.opportunity_id}`}>
            <div className="flex justify-between">
              <span className="overline text-[#CC5500]">{o.type}</span>
              <span className="text-xs text-neutral-500">{o.visibility}</span>
            </div>
            <h3 className="font-display font-bold mt-2">{o.title}</h3>
            <p className="text-neutral-400 text-sm mt-1">{o.short_description}</p>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setEditing(o)} className="text-sm text-[#CC5500]">Edit</button>
              <button onClick={() => remove(o.opportunity_id)} className="text-sm text-neutral-500 hover:text-red-400">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#141414] border border-white/10 max-w-lg w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="opp-editor">
            <h3 className="font-display text-xl font-bold mb-6">{editing.opportunity_id ? "Edit" : "New"} Opportunity</h3>
            <div className="space-y-4">
              <OppField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="opp-title" />
              <div className="grid grid-cols-2 gap-4">
                <OppSelect label="Type" value={editing.type} options={["CRE Listing", "Capital Opportunity", "Syndication", "Private Deal", "Media Offer", "Coaching Offer"]} onChange={(v) => setEditing({ ...editing, type: v })} />
                <OppSelect label="Visibility" value={editing.visibility} options={["public", "members", "private"]} onChange={(v) => setEditing({ ...editing, visibility: v })} />
              </div>
              <OppField label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} />
              <OppField label="Short Description" value={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} />
              <OppField label="Full Description" value={editing.full_description} onChange={(v) => setEditing({ ...editing, full_description: v })} />
              <OppField label="Price / Investment Range" value={editing.price_range} onChange={(v) => setEditing({ ...editing, price_range: v })} />
              <OppField label="CTA Link" value={editing.cta_link} onChange={(v) => setEditing({ ...editing, cta_link: v })} />
              <OppSelect label="Status" value={editing.status} options={["active", "under_review", "closed"]} onChange={(v) => setEditing({ ...editing, status: v })} />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} className="btn-primary flex-1 justify-center" data-testid="save-opp-btn"><Save size={16} /> Save</button>
              <button onClick={() => setEditing(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);

  const save = async () => {
    await api.put("/settings", { content: s.content, redirects: s.redirects, calendar: s.calendar, webhooks: s.webhooks, email: s.email });
    toast.success("Settings saved");
  };
  if (!s) return <p className="text-neutral-500">Loading…</p>;

  const sec = (obj, key, label) => (
    <div key={key}>
      <label className="overline text-neutral-400 mb-2 block">{label}</label>
      <input className="field-input" value={obj[key] ?? ""} onChange={(e) => setS({ ...s, [groupOf(key)]: { ...s[groupOf(key)], [key]: e.target.value } })} data-testid={`setting-${key}`} />
    </div>
  );
  const groupOf = (key) => {
    if (key in s.content) return "content";
    if (key in s.redirects) return "redirects";
    if (key in s.calendar) return "calendar";
    if (key in s.webhooks) return "webhooks";
    return "email";
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <Card title="Homepage Content">
        <div className="grid sm:grid-cols-2 gap-5">
          {sec(s.content, "hero_headline", "Hero Headline")}
          {sec(s.content, "primary_cta", "Primary CTA")}
          {sec(s.content, "secondary_cta", "Secondary CTA")}
          {sec(s.content, "contact_email", "Contact Email")}
        </div>
        <div className="mt-5">{sec(s.content, "hero_subheadline", "Hero Subheadline")}</div>
      </Card>

      <Card title="External Redirect URLs">
        <div className="grid sm:grid-cols-2 gap-5">
          {Object.keys(s.redirects).map((k) => sec(s.redirects, k, k.replace(/_/g, " ")))}
        </div>
      </Card>

      <Card title="Calendar Booking Links">
        <div className="grid sm:grid-cols-2 gap-5">
          {Object.keys(s.calendar).map((k) => sec(s.calendar, k, k.replace(/_/g, " ")))}
        </div>
      </Card>

      <Card title="GHL Webhook URLs">
        <div className="grid sm:grid-cols-2 gap-5">
          {Object.keys(s.webhooks).map((k) => sec(s.webhooks, k, k.replace(/_/g, " ")))}
        </div>
      </Card>

      <Card title="Email Notifications">
        <div className="grid sm:grid-cols-2 gap-5">
          {sec(s.email, "admin_notify_email", "Admin Notify Email")}
          {sec(s.email, "sender_email", "Sender Email")}
        </div>
        <p className="text-neutral-600 text-xs mt-3">Emails send when RESEND_API_KEY is configured in the backend environment.</p>
      </Card>

      <button onClick={save} className="btn-primary" data-testid="save-settings-btn"><Save size={16} /> Save All Settings</button>
    </div>
  );
}

// ---- small helpers ----
function Stat({ label, value }) {
  return <div className="card-edge p-6"><p className="overline text-neutral-500">{label}</p><p className="font-display text-4xl font-bold mt-2 text-[#CC5500]">{value}</p></div>;
}
function Row({ label, value }) {
  return <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-neutral-400">{label}</span><span className="font-semibold">{value}</span></div>;
}
function DetailRow({ label, value }) {
  return <div className="flex gap-3"><span className="text-neutral-500 capitalize w-32 shrink-0">{label}</span><span className="text-neutral-200">{value}</span></div>;
}
function Card({ title, children }) {
  return <div className="card-edge p-6"><Overline className="mb-5">{title}</Overline>{children}</div>;
}
function OppField({ label, value, onChange, testid }) {
  return <div><label className="overline text-neutral-400 mb-2 block">{label}</label><input className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
}
function OppSelect({ label, value, options, onChange }) {
  return <div><label className="overline text-neutral-400 mb-2 block">{label}</label><select className="field-input" value={value} onChange={(e) => onChange(e.target.value)}>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
}
